import { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import SportsEsportsRoundedIcon from '@mui/icons-material/SportsEsportsRounded';
import PhishingRoundedIcon from '@mui/icons-material/PhishingRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import NoteAltRoundedIcon from '@mui/icons-material/NoteAltRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import NewspaperRoundedIcon from '@mui/icons-material/NewspaperRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../../utils/axiosClient';
import './Header.css';

// JWT 토큰에서 사용자 이름 추출
const getUserNameFromToken = (): string | null => {
  try {
    const token = sessionStorage.getItem('avatye:ops:admin:accessToken');
    if (!token) return null;

    const payload = token.split('.')[1];
    if (!payload) return null;

    const decoded = JSON.parse(atob(payload));
    return decoded.sub || null;
  } catch {
    return null;
  }
};

// 로그아웃 처리
const handleLogout = async () => {
  // refreshToken 쿠키를 서버에서 무효화
  try {
    await apiClient.post('/api/v1/auth/logout');
  } catch {
    // 무효화 요청이 실패해도 클라이언트 로그아웃은 진행
  }

  // 세션 스토리지에서 토큰 삭제
  sessionStorage.removeItem('avatye:ops:admin:accessToken');

  // 로그인 페이지로 이동
  window.location.href = '/login';
};

export default function Header() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const location = useLocation();
  const navigate = useNavigate();

  // 사용자 정보 상태
  const [userName, setUserName] = useState<string | null>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const isUserMenuOpen = Boolean(userMenuAnchor);

  useEffect(() => {
    setUserName(getUserNameFromToken());
  }, []);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const navItems = [
    {
      label: '월말정산',
      path: '/settlement/rc',
      activeMatch: (p: string) => p.startsWith('/settlement'),
      subs: [
        {
          label: '월말정산',
          items: [
            { label: 'RC', path: '/settlement/rc' },
            { label: 'SSP', path: '/settlement/ssp' },
          ],
        },
      ],
    },
    {
      label: '포인트홈',
      path: '/pointhome',
      activeMatch: (p: string) => p.startsWith('/pointhome') || p === '/',
      subs: [
        {
          label: '포인트홈',
          items: [
            { label: '대시보드', path: '/pointhome' },
            { label: '정산 현황', path: '/pointhome/settlement/status' },
            { label: '오퍼월', path: '/pointhome/offerwall' },
            { label: '머니오락실', path: '/pointhome/moneyArcade' },
            { label: '쏠쏠한 오락실', path: '/pointhome/contentsArcade' },
            { label: '하루스토리', path: '/pointhome/harustory' },
          ],
        },
        {
          label: '룰렛',
          items: [
            { label: '매출 현황', path: '/pointhome/roulette/revenue' },
            { label: '콘텐츠 현황', path: '/pointhome/roulette/content' },
            { label: '전환 차트', path: '/pointhome/roulette/chart' },
            { label: '리텐션', path: '/pointhome/roulette/retention' },
          ],
        },
        {
          label: '애드팝콘',
          items: [
            { label: '애드팝콘 전체', path: '/pointhome/adpopcorn' },
            { label: '애드팝콘 상세', path: '/pointhome/adpopcorn/detail' },
          ],
        },
        {
          label: '버즈빌',
          items: [{ label: '버즈빌', path: '/pointhome/buzzvil' }],
        },
        {
          label: 'Ace Ent.',
          items: [{ label: 'Ace Ent.', path: '/pointhome/aceenter' }],
        },
        {
          label: '콘텐츠 지표',
          items: [
            { label: '콘텐츠 수익률', path: '/pointhome/content/metrics/profit-loss' },
            {
              label: '콘텐츠 참여율',
              path: '/pointhome/content/metrics/participation',
            },
            {
              label: '콘텐츠 현황',
              path: '/pointhome/content/metrics/status',
            },
          ],
        },
        {
          label: '사용자 지표',
          items: [
            { label: '리텐션', path: '/pointhome/user/metrics/retention' },
            { label: '웰컴 리텐션', path: '/pointhome/user/metrics/welcome/retention' },
          ],
        },
        {
          label: '실험실',
          items: [{ label: 'Dead-Click', path: '/pointhome/dead-click' }],
        },
      ],
    },
    {
      label: '럭키복권',
      path: '/lucky-lottery/ticket/received',
      activeMatch: (p: string) => p.startsWith('/lucky-lottery'),
      subs: [
        {
          label: '응모권 발급',
          items: [
            { label: '일일 응모권 발급 현황', path: '/lucky-lottery/ticket/received' },
            { label: '콘텐츠별 참여 현황', path: '/lucky-lottery/ticket/received/contents' },
            { label: '출석체크 참여 상세', path: '/lucky-lottery/ticket/received/attendance' },
          ],
        },
        {
          label: '복권 응모',
          items: [
            { label: '복권 응모 현황', path: '/lucky-lottery/enter' },
            { label: '복권 당첨 현황', path: '/lucky-lottery/winning' },
          ],
        },
        {
          label: '포인트 교환',
          items: [
            { label: '응모권 교환 현황', path: '/lucky-lottery/exchange/currency' },
            { label: 'Npay 포인트 교환 현황', path: '/lucky-lottery/exchange/npay' },
          ],
        },
        {
          label: '매출',
          items: [
            { label: '매출 현황', path: '/lucky-lottery/revenue' },
            { label: '매출 인식 현황', path: '/lucky-lottery/revenue/detail' },
            { label: '정산 현황', path: '/lucky-lottery/settlement/status' },
          ],
        },
        {
          label: '버즈빌',
          items: [{ label: '버즈빌', path: '/lucky-lottery/buzzvil' }],
        },
        {
          label: '애드팝콘',
          items: [
            { label: '애드팝콘 전체', path: '/lucky-lottery/adpopcorn' },
            { label: '애드팝콘 상세', path: '/lucky-lottery/adpopcorn/detail' },
          ],
        },
        {
          label: '사용자 지표',
          items: [{ label: '리텐션', path: '/lucky-lottery/retention' }],
        },
      ],
    },
    {
      label: '애드캐시',
      path: '/adcash/buzzvil',
      activeMatch: (p: string) => p.startsWith('/adcash'),
      subs: [
        {
          label: '버즈빌',
          items: [{ label: '버즈빌', path: '/adcash/buzzvil' }],
        },
        {
          label: '구루',
          items: [
            { label: 'NAM(앱)', path: '/adcash/guru/nam/app' },
            { label: 'NAM(웹)', path: '/adcash/guru/nam/web' },
            { label: 'ADforus', path: '/adcash/guru/adforus' },
            { label: 'TargetPick', path: '/adcash/guru/targetpick' },
            { label: 'Covi', path: '/adcash/guru/covi' },
          ],
        },
        {
          label: '아바티',
          items: [
            { label: '아바티 매출', path: '/adcash/avatye/revenue' },
            { label: '아바티 상세', path: '/adcash/avatye/detail' },
          ],
        },
        {
          label: '애드핏',
          items: [{ label: '애드핏 매출', path: '/adcash/adfit' }],
        },
      ],
    },
    {
      label: '네이버/뉴스',
      path: '/naver/revenue',
      activeMatch: (p: string) => p.startsWith('/naver'),
      subs: [
        {
          label: '네이버',
          items: [
            { label: '공급사 매출', path: '/naver/revenue' },
            { label: '공급사 소진량', path: '/naver/hourly/spent' },
            { label: '직연동 실시간 현황', path: '/naver/direct/realtime' },
            { label: '직연동 앱별 상세', path: '/naver/direct/daily' },
            // TODO: 퍼널데이터, 소진량 추가
            // { label: '퍼널데이터', path: '/naver/funnel' },
            // { label: '소진량', path: '/naver/consumption' },
          ],
        },
        {
          label: '뉴스',
          items: [
            { label: '뉴스 매출', path: '/naver/news/revenue' },
            { label: '매거진 상세', path: '/naver/news/magazine/detail' },
            { label: '직연동 실시간 현황', path: '/naver/news/direct/realtime' },
          ],
        },
      ],
    },
  ] as const;

  const getGroupIcon = (groupLabel: string) => {
    switch (groupLabel) {
      case '포인트홈':
      case '게임포탈':
        return <DashboardRoundedIcon fontSize="small" />;
      case '애드팝콘':
        return <AssessmentRoundedIcon fontSize="small" />;
      case '버즈빌':
        return <CampaignRoundedIcon fontSize="small" />;
      case 'Ace Ent.':
        return <AssessmentRoundedIcon fontSize="small" />;
      case '콘텐츠 지표':
        return <BarChartRoundedIcon fontSize="small" />;
      case '사용자 지표':
        return <PeopleAltRoundedIcon fontSize="small" />;
      case '실험실':
        return <InsightsRoundedIcon fontSize="small" />;
      case '구루':
        return <InsightsRoundedIcon fontSize="small" />;
      case '아바티':
        return <PaidRoundedIcon fontSize="small" />;
      case '게임':
        return <SportsEsportsRoundedIcon fontSize="small" />;
      case '퍼널':
        return <TimelineRoundedIcon fontSize="small" />;
      case '미션':
        return <FlagRoundedIcon fontSize="small" />;
      case '광고':
        return <CampaignRoundedIcon fontSize="small" />;
      case '메모G':
        return <NoteAltRoundedIcon fontSize="small" />;
      case '룰렛':
        return <PhishingRoundedIcon fontSize="small" />;
      case '애드핏':
        return <PaidRoundedIcon fontSize="small" />;
      case '네이버':
        return (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16.273 12.845L7.376 0H0v24h7.727V11.155L16.624 24H24V0h-7.727z" />
          </svg>
        );
      case '뉴스':
        return <NewspaperRoundedIcon fontSize="small" />;
      case '응모권 발급':
        return <ConfirmationNumberRoundedIcon fontSize="small" />;
      case '복권 응모':
        return <ConfirmationNumberRoundedIcon fontSize="small" />;
      case '포인트 교환':
        return <ConfirmationNumberRoundedIcon fontSize="small" />;
      case '매출':
        return <PaidRoundedIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const go = (path: string) => {
    if (location.pathname === path) return;
    navigate(path);
  };

  const [openMenuLabel, setOpenMenuLabel] = useState<string | null>(null);

  const handleTopClick = (label: string, path: string, hasSubs: boolean, isInSection: boolean) => {
    if (!hasSubs) {
      // 단일 메뉴(월말정산 등)는 섹션 안에 있다면 라우팅 유지, 아니면 이동
      setOpenMenuLabel(null);
      if (!isInSection) {
        go(path);
      }
      return;
    }

    // 데스크탑: 섹션 바깥에서 클릭하면 메인으로 이동 + 메가 메뉴 열기
    // 섹션 안에서 클릭하면 경로는 유지하고 메가 메뉴만 토글
    const willOpen = openMenuLabel !== label;
    setOpenMenuLabel(willOpen ? label : null);
    if (!isInSection) {
      go(path);
    }
  };

  const openItem = navItems.find((item) => item.label === openMenuLabel) ?? null;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          height: 'auto',
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#ffffff',
          color: '#1e293b',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Toolbar
          sx={{
            px: 3,
            minHeight: { xs: 56, md: 64 },
            gap: 0,
          }}
        >
          {/* 로고 + 브랜드 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mr: 5,
              flexShrink: 0,
            }}
          >
            <img src="/company-logo.svg" alt="Logo" style={{ height: 28, objectFit: 'contain' }} />
            <Box
              sx={{
                width: '1px',
                height: 24,
                bgcolor: '#e2e8f0',
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.95rem',
                color: '#64748b',
                whiteSpace: 'nowrap',
              }}
            >
              애널리틱스
            </Typography>
          </Box>

          {/* 네비게이션 메뉴 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {navItems.map((item) => {
              const hasSubs = (item.subs?.length ?? 0) > 0;
              const isOpen = openMenuLabel === item.label;
              const isInSection = item.activeMatch(location.pathname);
              const active = isOpen || isInSection;
              return (
                <Box
                  key={item.label}
                  component="button"
                  type="button"
                  onClick={() =>
                    isDesktop
                      ? handleTopClick(item.label, item.path, hasSubs, isInSection)
                      : go(item.path)
                  }
                  sx={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    outline: 'none',
                    '&:focus-visible': {
                      outline: 'none',
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.3,
                      px: 1.4,
                      py: 0.7,
                      borderRadius: 2,
                      backgroundColor: active ? '#f1f5f9' : 'transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        backgroundColor: '#f8fafc',
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        fontWeight: active ? 600 : 500,
                        color: active ? '#0044D2' : '#475569',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.label}
                    </Typography>
                    {hasSubs && isDesktop && (
                      <KeyboardArrowDownRoundedIcon
                        sx={{
                          fontSize: 18,
                          color: active ? '#0044D2' : '#94a3b8',
                          transition: 'transform 0.2s ease',
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* 사용자 메뉴 */}
          {userName && (
            <>
              <Box
                onClick={handleUserMenuOpen}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ml: 2,
                  pl: 2,
                  borderLeft: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  '&:hover': {
                    opacity: 0.7,
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#0044D2',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                  }}
                >
                  <PersonRoundedIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography
                  sx={{
                    color: '#1e293b',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    display: { xs: 'none', md: 'block' },
                  }}
                >
                  {userName}
                </Typography>
                <KeyboardArrowDownRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: '#94a3b8',
                    display: { xs: 'none', md: 'flex' },
                  }}
                />
              </Box>

              <Menu
                anchorEl={userMenuAnchor}
                open={isUserMenuOpen}
                onClose={handleUserMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{
                  paper: {
                    elevation: 4,
                    sx: {
                      mt: 1.5,
                      borderRadius: 2.5,
                      minWidth: 200,
                      overflow: 'visible',
                      '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 20,
                        width: 10,
                        height: 10,
                        bgcolor: 'background.paper',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                      },
                    },
                  },
                }}
              >
                {/* 사용자 정보 */}
                <Box
                  sx={{
                    px: 2,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: '#0044D2',
                      color: '#fff',
                    }}
                  >
                    <PersonRoundedIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: '#1e293b',
                      }}
                    >
                      {userName}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#64748b',
                      }}
                    >
                      AVATYE ANALYTICS
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                {/* 로그아웃 */}
                <MenuItem
                  onClick={() => {
                    handleUserMenuClose();
                    handleLogout();
                  }}
                  sx={{
                    py: 1.2,
                    px: 2,
                    mx: 1,
                    mb: 1,
                    gap: 1.5,
                    borderRadius: 1.5,
                    color: '#dc2626',
                    '&:hover': {
                      bgcolor: 'rgba(220, 38, 38, 0.08)',
                    },
                  }}
                >
                  <LogoutRoundedIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>로그아웃</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>

        {/* 데스크탑 메가 메뉴 패널 (CSS transition 기반) */}
        {isDesktop && (
          <Box
            className={
              !!openItem && (openItem.subs?.length ?? 0) > 0
                ? 'mega-nav mega-nav--open'
                : 'mega-nav'
            }
          >
            <Box
              sx={{
                width: '100%',
                px: 3, // 헤더 상단과 동일한 좌우 여백
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                  lg: 'repeat(4, minmax(0, 1fr))',
                  xl: 'repeat(5, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {(openItem?.subs ?? []).map((sub) => {
                // 포인트홈처럼 그룹 구조인 경우: 제목 + 내부 선택지들
                if ('items' in sub && Array.isArray(sub.items) && sub.items.length > 0) {
                  const items = sub.items;
                  const groupIcon = getGroupIcon(sub.label);
                  return (
                    <Box
                      key={sub.label}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        textAlign: 'left',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          color: '#1e293b',
                          mb: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {groupIcon && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#0044D2',
                            }}
                          >
                            {groupIcon}
                          </Box>
                        )}
                        <span>{sub.label}</span>
                      </Typography>

                      {items.map((item) => {
                        const active = location.pathname === item.path;
                        return (
                          <Box
                            key={item.label}
                            component="button"
                            type="button"
                            onClick={() => {
                              setOpenMenuLabel(null);
                              go(item.path);
                            }}
                            sx={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              textAlign: 'left',
                              cursor: 'pointer',
                              outline: 'none',
                              '&:focus-visible': {
                                outline: 'none',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderRadius: 1.5,
                                px: 1,
                                py: 0.6,
                                bgcolor: active ? '#f1f5f9' : 'transparent',
                                transition: 'background-color 0.15s ease',
                                '& .mega-nav-item-arrow': {
                                  opacity: 0,
                                  transform: 'translateX(-4px)',
                                  transition: 'opacity 0.2s ease, transform 0.2s ease',
                                },
                                '&:hover': {
                                  bgcolor: '#f8fafc',
                                  '& .mega-nav-item-arrow': {
                                    opacity: 1,
                                    transform: 'translateX(0)',
                                  },
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '0.88rem',
                                  fontWeight: active ? 600 : 400,
                                  color: active ? '#0044D2' : '#475569',
                                }}
                              >
                                {item.label}
                              </Typography>
                              <Box
                                className="mega-nav-item-arrow"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  color: '#94a3b8',
                                }}
                              >
                                <ArrowForwardIosRoundedIcon sx={{ fontSize: 12 }} />
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                }

                // 기존 구조(단일 링크)인 경우
                if ('path' in sub) {
                  const isExact = location.pathname === sub.path;
                  return (
                    <Box
                      key={sub.label}
                      component="button"
                      type="button"
                      onClick={() => {
                        setOpenMenuLabel(null);
                        go(sub.path as string);
                      }}
                      sx={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textAlign: 'left',
                        cursor: 'pointer',
                        outline: 'none',
                        '&:focus-visible': {
                          outline: 'none',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderRadius: 1.5,
                          px: 1.2,
                          py: 0.8,
                          bgcolor: isExact ? '#f1f5f9' : 'transparent',
                          border: '1px solid',
                          borderColor: isExact ? '#e2e8f0' : 'transparent',
                          transition: 'background-color 0.15s ease, border-color 0.15s ease',
                          '& .mega-nav-item-arrow': {
                            opacity: 0,
                            transform: 'translateX(-4px)',
                            transition: 'opacity 0.2s ease, transform 0.2s ease',
                          },
                          '&:hover': {
                            bgcolor: '#f8fafc',
                            borderColor: '#e2e8f0',
                            '& .mega-nav-item-arrow': {
                              opacity: 1,
                              transform: 'translateX(0)',
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              fontWeight: isExact ? 600 : 500,
                              color: isExact ? '#0044D2' : '#1e293b',
                              mb: 0.2,
                            }}
                          >
                            {sub.label}
                          </Typography>
                          {(() => {
                            const descriptionText =
                              'description' in sub && typeof sub.description === 'string'
                                ? sub.description
                                : `${sub.label} 화면으로 이동`;
                            return (
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: '#94a3b8',
                                  lineHeight: 1.4,
                                }}
                              >
                                {descriptionText}
                              </Typography>
                            );
                          })()}
                        </Box>
                        <Box
                          className="mega-nav-item-arrow"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: '#94a3b8',
                          }}
                        >
                          <ArrowForwardIosRoundedIcon sx={{ fontSize: 12 }} />
                        </Box>
                      </Box>
                    </Box>
                  );
                }

                return null;
              })}
            </Box>
          </Box>
        )}
      </AppBar>
    </>
  );
}
